<template>
    <v-menu v-model="resourcePoolMenu" offset-x nudge-top="8" :close-on-content-click="false">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingAvailableResourcePools"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeResourcePoolId)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : 'mdi-chart-box-outline' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingAvailableResourcePools">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title class="d-flex align-center">
                                <b>{{ resourcePoolName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="resourcePoolActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToPoolSettings">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <ResourcePoolInviteDialog v-if="activeResourcePoolId">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-list-item v-bind="attrs" v-on="on" @click="resourcePoolActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </ResourcePoolInviteDialog>
                                    <v-divider class="my-2"></v-divider>
                                    <v-list-item @click="resourcePoolActionsMenu = false">
                                        <v-list-item-icon>
                                            <v-icon color="error">mdi-trash-can-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="error--text mr-4">Delete resource pool</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ resourcePoolName }}</span>
            </v-tooltip>
        </template>

        <v-list nav dense min-width="300" class="pb-0">
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-text-field
                        v-model="resourcePoolSearch"
                        placeholder="Filter resource pools..."
                        background-color="grey lighten-4"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense></v-text-field>
                </v-list-item-title>
            </v-list-item>
            <v-list-item>
                <v-list-item-title>RESOURCE POOLS</v-list-item-title>
            </v-list-item>
        </v-list>

        <v-list dense nav v-if="sortedResourcePools.length" class="resource-pool-list-scroll pt-0">
            <v-list-item v-for="item in sortedResourcePools" :key="item.rpid" @click="openResourcePool(item.rpid)">
                <v-list-item-content>
                    <v-list-item-title class="d-flex align-center" :class="item.deleted && 'deleted'">
                        {{ item.long_id }}
                    </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">
                                {{ organizationRole(item.is_rp_manager ? 'manager' : 'member').icon }}
                            </v-icon>
                        </template>
                        <span>{{ organizationRole(item.is_rp_manager ? 'manager' : 'member').label }}</span>
                    </v-tooltip>
                </v-list-item-action>
            </v-list-item>
        </v-list>

        <v-list v-else-if="resourcePoolSearch && sortedResourcePools.length === 0" class="pb-4">
            <v-list-item>
                <v-list-item-title>
                    <DashboardEmpty icon="mdi-information-outline">
                        No matching resource pools found.
                        <br />
                        Try a different filter.
                    </DashboardEmpty>
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script>
import { mapState } from 'vuex'
import { organizationRole } from '@/utils'

const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const ResourcePoolInviteDialog = () => import('@/modules/user/components/ResourcePoolInviteDialog.vue')

export default {
    name: 'ResourcePoolMenu',
    components: {
        DashboardEmpty,
        ResourcePoolInviteDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            resourcePoolMenu: false,
            resourcePoolSearch: '',
            resourcePoolActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapState(['userOrgs', 'currentOrgData']),
        ...mapState('userStore', ['fetchingAvailableResourcePools', 'availableResourcePools', 'selectedResourcePool']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        activeResourcePoolId() {
            return Number(this.$route.params.rpid || this.selectedResourcePool || this.availableResourcePools[0]?.rpid)
        },
        activeResourcePool() {
            return this.availableResourcePools.find(item => Number(item.rpid) === this.activeResourcePoolId)
        },
        resourcePoolName() {
            return this.activeResourcePool?.long_id || 'Select resource pool'
        },
        sortedResourcePools() {
            if (this.resourcePoolSearch.length) {
                return this.availableResourcePools.filter(item => item.long_id.toLowerCase().includes(this.resourcePoolSearch.toLowerCase()))
            }

            return this.availableResourcePools
        }
    },
    methods: {
        organizationRole,
        goToPoolSettings() {
            this.resourcePoolActionsMenu = false
            if (!this.currentOrgOid || !this.activeResourcePoolId) {
                return
            }

            this.$router.push({
                name: 'config-pool-general',
                params: {
                    oid: this.currentOrgOid,
                    rpid: this.activeResourcePoolId
                }
            })
        },
        openResourcePool(rpid) {
            this.resourcePoolMenu = false

            const routeName = this.$route.name || ''
            if ((routeName === 'config-root' || routeName.startsWith('config-')) && this.currentOrgOid) {
                this.$router.push({
                    name: 'config-pool-general',
                    params: {
                        oid: this.currentOrgOid,
                        rpid
                    }
                })
                return
            }

            this.$router.push({
                name: 'reporting-rp-overview',
                params: {
                    rpid,
                    startDate: this.$route.params.startDate,
                    endDate: this.$route.params.endDate
                }
            })
        },
        syncResourcePoolState() {
            const managedResourcePools = this.availableResourcePools.filter(item => item.is_rp_manager)
            this.$store.dispatch('userStore/setManagedResourcePools', managedResourcePools)

            const selectedResourcePoolId = Number(this.$route.params.rpid || this.selectedResourcePool)
            if (Number.isFinite(selectedResourcePoolId) && selectedResourcePoolId > 0) {
                this.$store.dispatch('userStore/setSelectedResourcePool', selectedResourcePoolId)
            }

            const selectedResourcePool = this.availableResourcePools.find(item => Number(item.rpid) === selectedResourcePoolId) || null
            this.$store.dispatch('userStore/setSelectedRP', selectedResourcePool)
            this.$store.dispatch('userStore/setRpManager', Boolean(selectedResourcePool?.is_rp_manager))
        }
    },
    watch: {
        '$route.params.rpid': {
            handler() {
                this.syncResourcePoolState()
            },
            immediate: true
        },
        availableResourcePools: {
            handler() {
                this.syncResourcePoolState()
            },
            immediate: true
        },
        resourcePoolMenu(val) {
            if (val) {
                this.resourcePoolSearch = ''
            }
        }
    },
    created() {
        if (!this.availableResourcePools.length && !this.fetchingAvailableResourcePools) {
            this.$store.dispatch('userStore/fetchAvailableResourcePoolData')
        }
    }
}
</script>

<style scoped lang="scss">
.resource-pool-list-scroll {
    max-height: 60vh;
    overflow-y: auto;
}

.deleted {
    text-decoration: line-through;
    opacity: 0.5;
}
</style>
